import {
  Box,
  Card,
  CardContent,
  Container,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { ArrowForward } from "@material-ui/icons";
import { FormBuilder } from "@zennya/web-component-library/src/components/FormBuilder";
import { ConcentricBadge } from "components/ConcentricBadge";
import { useFormikContext } from "formik";
import { motion } from "framer-motion";
import { debounce, isEmpty } from "lodash";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { JsonParam, ObjectParam, StringParam } from "serialize-query-params";
import corporateHealthService from "services/corporate-health.service";
import { useQueryParam } from "use-query-params";
import { processHealthPass } from "util/ProcessHealthPass";
import { slideRight, slideUp } from "util/transitions";
import * as Yup from "yup";

import corphealthService from 'services/corporate-health.service'
import clsx from "clsx";

export const useStyles = makeStyles((theme) => ({
  prefilled: {
    backgroundColor: theme.palette.grey[100],
  },
  default: {
    [theme.breakpoints.down("xs")]: {
      backgroundColor: theme.palette.common.white,
    },
  },
  container: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gridGap: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  headerContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  header: {
    ...theme.typography.h5,
    paddingLeft: theme.spacing(2),
    fontWeight: 700,
  },
  card: {
    borderRadius: 20,
    [theme.breakpoints.down("xs")]: {
      backgroundColor: theme.palette.background.card,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  cardInset: {
    [theme.breakpoints.down("xs")]: {
      paddingRight: 0,
    },
    overflowX: "hidden",
    paddingBottom: theme.spacing(1),
  },
  eula: {
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
  },
  scrollBoxContainer: {
    padding: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  scrollBox: {
    maxHeight: "220px",
    overflowY: "scroll",
  },
  cutout: {
    width: "100%",
    height: "1px",
    backgroundImage: `linear-gradient(to right, transparent 50%, ${theme.palette.divider} 50%)`,
    backgroundSize: "15px 100%",
  },
  actionBottom: {
    justifyContent: "flex-end",
  },
  eulaCheck: {
    justifyContent: "center",
  },
  prepend:{
    paddingBottom: theme.spacing(2)
  },
  edge:{
    borderTop: theme.palette.text.secondary
  }
}));

const formLayout = [
  ["firstName", "middleName"],
  "lastName",
  ["birthDate", "gender"],
  "divider",
  "eula",
  "eulaCheck",
];

function MedicalIdForm({privacy=''}) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [urlFormValues, setUrlFormValues] = useQueryParam(
    "medicalFormValues",
    JsonParam
  );

  const [formValues, setFormValues] = useState(urlFormValues);

  const debounceParams = useCallback(
    debounce(
      (values) => {
        setUrlFormValues(values, "replaceIn");
      },
      250,
      { maxWait: 500 }
    ),
    []
  );

  useEffect(() => {
    debounceParams(formValues);
  }, [debounceParams, formValues]);

  // useEffect(() => {
  //   async function validateBirthDay(){
  //     const bdayValidator = Yup.date().max(new Date()).required()
  //     const bdayValidate = await bdayValidator.validate(formValues.birthDate)
  //   }
  //   validateBirthDay();
  // }, []);
  const form = useMemo(() => {
    
    const formItem = {
      firstName: {
        type: "text",
        label: "First Name",
        initialValues: formValues?.firstName ?? "",
        validator: () => Yup.string().required().nullable().label(" "),
        readOnly: !!formValues?.firstName,
        fieldProps: {
          className: formValues?.firstName
            ? classes.prefilled
            : classes.default,
        },
      },
      middleName: {
        type: "text",
        label: "Middle Name",
        initialValues: formValues?.middleName ?? "",
        validator: () => Yup.string().nullable().label(" "),
        readOnly: !!formValues?.middleName,
        fieldProps: {
          className: formValues?.middleName
            ? classes.prefilled
            : classes.default,
        },
      },
      lastName: {
        type: "text",
        label: "Last Name",
        initialValues: formValues?.lastName ?? "",
        validator: () => Yup.string().required().nullable().label(" "),
        readOnly: !!formValues?.lastName,
        fieldProps: {
          className: formValues?.lastName ? classes.prefilled : classes.default,
        },
      },
      birthDate: {
        type: "date",
        label: "Birth Date",
        initialValues:
          formValues?.birthDate ?? moment().tz("Asia/Manila").subtract(15, "years").toDate(),
        useLocalTime: true,
        validator: () => Yup.date().max(new Date()).min(new Date('1900-01-01')).nullable().label(" "),
        // readOnly: !!formValues?.birthDate,
        fieldProps: {
          className: classes.default,
          // maxDate: moment().tz("Asia/Manila").subtract(15, "years").toDate(),
        },
      },
      gender: {
        type: "select",
        label: "Gender",
        initialValues: formValues?.gender ?? "",
        options: [
          { value: "Any", label: "Unspecified" },
          { value: "Male", label: "Male" },
          { value: "Female", label: "Female" },
        ],
        settings: {
          default: "",
          multiple: false,
          labelField: "label",
          valueField: "value",
        },
        validator: () => Yup.string().required().nullable().label(" "),
        readOnly: !!formValues?.gender,
        fieldProps: {
          className: formValues?.gender ? classes.prefilled : classes.default,
        },
      },
      eulaCheck: {
        type: "checkbox",
        label: "I agree to the data policy above",
        initialValues: formValues?.eulaCheck ?? false,
        settings: {
          labelField: "label",
          valueField: "id",
        },
        validator: () =>
          Yup.bool().oneOf([true], "Accept Data Sharing Agreement is required"),
        fieldProps: {
          className: classes.eulaCheck,
        },
      },
      divider: {
        type: "component",
        component: () => <div className={classes.cutout} />,
      },
      eula: {
        type: "component",
        component: () => (
          <Paper className={classes.scrollBoxContainer}>
            <Box className={classes.scrollBox}>
              { !isEmpty(privacy) && <><div className={clsx(classes.eula, classes.prepend)} dangerouslySetInnerHTML={{__html:privacy}}/><hr className={classes.edge}/></>}
              <div className={classes.eula}>Zennya Health Data Policy</div>
              <br/>
              <div className={classes.eula}>1. Patient Data Use Agreement</div>
              <div className={classes.eula}>
                1.1 You consent to enter into this Patient Data Use Agreement
                (PDUA or Agreement), by and between yourself (Patient) and
                zennya, Inc. (Patient Data Manager, or PDM), to authorizes PDM,
                on Patient’s behalf, to request, acquire, receive, aggregate,
                maintain, curate, secure, share, and delete, with Patient’s
                permission as granted pursuant to this Agreement, Patient’s
                complete, longitudinal digital health record (or any portions of
                the health record designated by the Patient). The parties are
                hereinafter referred collectively as the “Parties” and
                individually as a “Party.”
              </div>
              <div className={classes.eula}>2. Background and Authority</div>
              <div className={classes.eula}>
                2.1. The Health Insurance Portability and Accountability Act of
                1996 (HIPAA), as amended, provides individuals with a right of
                access to inspect and obtain a copy of protected health
                information from their medical records maintained by their
                healthcare providers [see 45 CFR 164.524(a)(1)]. Under this
                right of access, pursuant to 45 CFR 164.524(c)(3)(iii),
                individuals can request, in a signed writing identifying where
                and to whom, that their personal health information be provided
                to third parties on the patients’ behalf.[^1] Using this right
                of access, an individual can use a third-party patient data
                manager to aggregate a complete, longitudinal record and
                maintain it in a way to provide secure access to accurate,
                reliable personal health data.
              </div>
              <div className={classes.eula}>
                2.2 Patient wishes to collect personal health data from a
                variety of providers and sources, including non-clinical sources
                and patient-generated sources; store that data in one complete,
                longitudinal record; and exert control over the sharing of and
                access to such health information.
              </div>
              <div className={classes.eula}>
                2.3 PDM has the capacity to aggregate, maintain, and secure
                personal health data in a way that enables it to be: regularly
                updated; protected; compartmentalized; shared in whole or in
                part with the Patient’s authorization; and maintained free of
                unauthorized changes or interference that could render the data
                untrustworthy.
              </div>
              <div className={classes.eula}>
                2.4 Patient seeks to exert the right of access provided to
                Patient by 45 CFR § 164.524 and related HHS Office for Civil
                Rights guidance to regularly access personal
              </div>
            </Box>
          </Paper>
        ),
      },
    };
    return formItem;
  }, [
    classes.cutout,
    classes.default,
    classes.eula,
    classes.eulaCheck,
    classes.prefilled,
    classes.scrollBox,
    classes.scrollBoxContainer,
    formValues?.birthDate,
    formValues?.eulaCheck,
    formValues?.firstName,
    formValues?.gender,
    formValues?.lastName,
    formValues?.middleName,
  ]);

  async function handleSubmit() {
    console.log("📢[index.js:157]:", "handleSubmit");
    history.push({
      pathname: "/onboarding/linkid",
      search: location.search,
    });
  }

  async function handleReset() {
    console.log("📢[index.js:163]:", "handleSubmit");
  }

  const FormConsumer = (props) => {
    const { values, initialValues, setValues, setFieldValue, ...formik } =
      useFormikContext();
    return null;
  };

  return (
    <Container fixed maxWidth="md" className={classes.cardInset}>
      <Box className={classes.headerContainer}>
        <ConcentricBadge number="1" />
        <span className={classes.header}>Medical ID</span>
      </Box>
      <Typography gutterBottom variant="caption">
        The information in this form is provided by your company. Kindly fill up
        the fields with missing information to register in the event.
      </Typography>
      <Card className={classes.card}>
        <CardContent>
          <FormBuilder
            formId="medicalId"
            // usePersist
            onChange={(values) => {
              setFormValues(values);
            }}
            variant="outlined"
            formLabel=" "
            submitLabel={
              <>
                Next <ArrowForward fontSize="small" />
              </>
            }
            formFactor="default"
            formLayout={formLayout}
            form={form}
            hasReset={false}
            onSubmit={(values) => {
              // console.log("📢[index.js:101 form submit]:", values);
              handleSubmit();
            }}
          >
            <FormConsumer />
          </FormBuilder>
        </CardContent>
      </Card>
    </Container>
  );
}

export default function MedicalIdLoader() {
  const [loading, setLoading] = useState(true);
  const [hashId, setHashId] = useQueryParam("code", StringParam);
  const [partnerPrivacy, setPartnerPrivacy] = useState('');

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"), {
    noSsr: true,
  });
  const [urlFormValues, setUrlFormValues] = useQueryParam(
    "medicalFormValues",
    JsonParam
  );

  useEffect(() => {
    async function fetchHealthPass() {
      try {
        const healthPass = await processHealthPass(hashId);
        const results = (await corporateHealthService.getPartnerPrivacy(healthPass?.partnerId)).data
        setPartnerPrivacy(results?.message);
        if (isEmpty(urlFormValues)) {
          setUrlFormValues(healthPass?.medicalId, "replaceIn");
        }

      } catch (error) {
        console.log("📢[index.js:393]:", error);
        
      } finally {
        setLoading(false);  
      }

      
    }
    fetchHealthPass();
  }, [hashId, setUrlFormValues]);

  if (loading) {
    return <LinearProgress />;
  }
  return (
    <motion.div {...(isMobile ? slideRight : slideUp)}>
      <MedicalIdForm privacy={partnerPrivacy}/>
    </motion.div>
  );
}
